import { useState, useEffect, useMemo } from "react";
import { DatePicker, Radio } from "antd";
import type { Dayjs } from "dayjs";
import FilterFooter from "./FilterFooter";
import {
  getRelativeDatePresetFromRange,
  getRelativeDatePresetRange,
  mapConditionValue,
  mapDisplayValue,
  RELATIVE_DATE_PRESETS,
} from "./filtersUtil";
import type { DateFilterMode, RelativeDatePreset } from "./filterTypes";

const { RangePicker } = DatePicker;

type FilterCalendarProps = {
  onClose: () => void;
  onApply?: (filterData: {
    condition: string | null;
    date: Dayjs | null;
    range: [Dayjs, Dayjs] | null;
    mode: DateFilterMode;
    relativePreset: RelativeDatePreset | null;
  }) => void;
  onDelete?: () => void;
  initialCondition?: string | null;
  initialDate?: Dayjs | null;
  initialRange?: [Dayjs, Dayjs] | null;
  initialMode?: DateFilterMode | null;
  initialRelativePreset?: RelativeDatePreset | null;
  isActiveFilter?: boolean;
  isDropdown?: boolean;
  hideFooter?: boolean;
  useAlternativeLayout?: boolean;
  disabled?: boolean;
};

const FilterCalendar = ({
  onClose,
  onApply,
  onDelete,
  initialCondition = null,
  initialDate = null,
  initialRange = null,
  initialMode = null,
  initialRelativePreset = null,
  isActiveFilter = false,
  isDropdown = false,
  hideFooter = false,
  useAlternativeLayout = false,
  disabled = false,
}: FilterCalendarProps) => {
  const isRelative = initialMode === "relative";
  const initialValue = isRelative
    ? "relative"
    : mapDisplayValue(initialCondition ?? "equals");

  const initialSelectedRange =
    isRelative && initialRelativePreset
      ? getRelativeDatePresetRange(initialRelativePreset)
      : initialRange;

  const [value, setValue] = useState<string | null>(initialValue);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(
    isRelative ? null : initialDate,
  );
  const [selectedRange, setSelectedRange] = useState<[Dayjs, Dayjs] | null>(
    initialSelectedRange,
  );

  const [showRangeBox, setShowRangeBox] = useState<boolean>(
    initialCondition === "between" || isRelative,
  );

  const [showInputBox, setShowInputBox] = useState<boolean>(
    !isRelative && initialValue !== "between" && initialValue !== "no_data",
  );

  const relativePresets = useMemo(
    () =>
      RELATIVE_DATE_PRESETS.map(({ value, label }) => ({
        label: (
          <span
            className={`flex w-full items-center rounded px-2 py-1 ${
              selectedRange &&
              getRelativeDatePresetFromRange(selectedRange) === value
                ? "bg-[#D2EFF9] font-semibold text-[#064C68]"
                : ""
            }`}
          >
            {label}
          </span>
        ),
        value: getRelativeDatePresetRange(value),
      })),
    [selectedRange],
  );

  useEffect(() => {
    if (isActiveFilter) {
      const nextValue = isRelative
        ? "relative"
        : mapDisplayValue(initialCondition ?? "equals");

      setValue(nextValue);
      setShowRangeBox(nextValue === "between" || nextValue === "relative");
      setShowInputBox(
        nextValue !== "between" &&
          nextValue !== "relative" &&
          nextValue !== "no_data",
      );

      if (nextValue === "relative" && initialRelativePreset) {
        setSelectedRange(getRelativeDatePresetRange(initialRelativePreset));
        setSelectedDate(null);
      } else if (nextValue === "relative" && initialRange) {
        setSelectedRange(initialRange);
        setSelectedDate(null);
      } else if (nextValue === "between" && initialRange) {
        setSelectedRange(initialRange);
        setSelectedDate(null);
      } else if (initialDate) {
        setSelectedDate(initialDate);
        setSelectedRange(null);
      }
    }
  }, [
    isActiveFilter,
    initialCondition,
    initialDate,
    initialRange,
    isRelative,
    initialRelativePreset,
  ]);

  const onChangeRadioButton = (e) => {
    const newValue = e.target.value;
    setValue(newValue);

    if (newValue === "relative") {
      setShowRangeBox(true);
      setShowInputBox(false);
      setSelectedDate(null);
      setSelectedRange(null);
    } else if (newValue === "between") {
      setShowRangeBox(true);
      setShowInputBox(false);
      setSelectedDate(null);
    } else if (newValue === "no_data") {
      setShowRangeBox(false);
      setShowInputBox(false);
      setSelectedDate(null);
    } else {
      setShowInputBox(true);
      setShowRangeBox(false);
      setSelectedRange(null);
    }
  };

  const onChangeDate = (date: Dayjs | null) => {
    setSelectedDate(date);
  };

  const onChangeRange = (dates: [Dayjs, Dayjs] | null) => {
    setSelectedRange(dates);
  };

  const handleClear = () => {
    setValue(null);
    setSelectedDate(null);
    setSelectedRange(null);
    setShowRangeBox(false);
    setShowInputBox(false);
  };

  const handleApply = () => {
    const relativePreset =
      value === "relative" && selectedRange
        ? getRelativeDatePresetFromRange(selectedRange)
        : null;
    const mode: DateFilterMode = value === "relative" ? "relative" : "absolute";
    const filterData = {
      condition: value === "relative" ? "between" : mapConditionValue(value),
      date: selectedDate,
      range: selectedRange,
      mode,
      relativePreset,
    };
    if (onApply) onApply(filterData);
  };

  const isApplyDisabled =
    !value ||
    (showInputBox && !selectedDate) ||
    (showRangeBox && (!selectedRange || selectedRange.length !== 2));

  return (
    <div className="flex flex-col h-full min-h-[10px]">
      <div className="flex flex-col w-full gap-3 flex-1">
        <p className="mt-2">Choose a condition</p>
        <Radio.Group
          vertical
          value={value}
          options={[
            { value: "on", label: "On" },
            { value: "before", label: "Before" },
            { value: "after", label: "After" },
            { value: "between", label: "Between" },
            { value: "relative", label: "Relative to" },
            { value: "no_data", label: "Null" },
          ]}
          onChange={onChangeRadioButton}
          disabled={disabled}
        />

        {showInputBox && (
          <div className="flex flex-col gap-2 mb-2">
            <p className="mt-2">Enter a date</p>
            <DatePicker
              value={selectedDate}
              onChange={onChangeDate}
              style={{ width: "100%" }}
              disabled={disabled}
            />
          </div>
        )}

        {showRangeBox && (
          <div className="flex flex-col gap-2 mb-2">
            <p className="mt-2">Enter date range</p>
            <RangePicker
              value={selectedRange}
              onChange={onChangeRange}
              style={{ width: "100%" }}
              disabled={disabled}
              presets={relativePresets}
            />
            {value === "relative" &&
              selectedRange &&
              !getRelativeDatePresetFromRange(selectedRange) && (
                <p className="w-fit rounded border border-[#E8B84A] bg-[#FFF8E1] px-2 py-1 text-xs font-semibold text-[#8A5A00]">
                  Custom relative range
                </p>
              )}
          </div>
        )}
      </div>

      {!hideFooter && (
        <FilterFooter
          onClose={onClose}
          onClear={handleClear}
          onApply={handleApply}
          onDelete={onDelete}
          isApplyDisabled={isApplyDisabled}
          isDropdown={isDropdown}
          useAlternativeLayout={useAlternativeLayout}
        />
      )}
    </div>
  );
};

export default FilterCalendar;
